import { addDays, differenceInCalendarDays, format } from 'date-fns';
import { parseDateOnly } from '../../../../../../utils/timeUtils';

/**
 * PRX-130 — the vacation supply rules (Rafik's vacRules), as a pure engine.
 *
 * Every date is a YYYY-MM-DD pharmacy-day string. Dates are parsed with
 * `parseDateOnly` (local midnight) and written back with
 * `format(…, 'yyyy-MM-dd')`, never through `toISOString` or `new Date('YYYY-MM-DD')`.
 *
 * Nothing is assumed: a missing trip input stops the whole plan with the
 * reason, and a row without a daily dose or a quantity on hand is
 * "Can't calculate" with the reason. The interpretations still open with Rafik
 * are in ADR-115 and the PRX-130 ticket; change `VACATION_RULES_VERSION` with
 * any change to the rule table or its reading.
 */
export const VACATION_RULES_VERSION = '2026-10-13.1';

/** WP-10 (PRX-130 Q4): the buffer the calculator starts with at a Fillware pharmacy. */
export const DEFAULT_BUFFER_DAYS = 7;

/**
 * WP-10 (PRX-130 Q6): what "ODB vacation supply already used this benefit
 * year" does. `refuse` is the rule as first built (an error that blocks the
 * request); `cap` is Rafik's answer of 7 Oct 2026 for PharmAssist: limit the
 * supply to 100 days, in and outside Ontario, and never refuse. The caller
 * picks it from the pharmacy system; the engine has no default.
 */
export type UsedSupplyPolicy = 'refuse' | 'cap';

/** A patient can always take up to 100 days; the vacation supply is anything above that. */
export const USED_SUPPLY_CAP_DAYS = 100;

export type Coverage = 'private' | 'odb' | 'tdp' | 'cash';
export type Destination = 'in_ontario' | 'outside_ontario';

export const COVERAGE_LABELS: Record<Coverage, string> = {
  private: 'Private',
  odb: 'ODB',
  tdp: 'Trillium (TDP)',
  cash: 'Cash',
};

export const DESTINATION_LABELS: Record<Destination, string> = {
  in_ontario: 'In Ontario',
  outside_ontario: 'Outside Ontario',
};

/** The rule table as staff read it, one line per coverage and destination. */
export const VACATION_RULE_TABLE: ReadonlyArray<{ coverage: Coverage; destination: Destination; rule: string }> = [
  { coverage: 'cash', destination: 'in_ontario', rule: 'Up to 365 days' },
  { coverage: 'cash', destination: 'outside_ontario', rule: 'Up to 365 days' },
  { coverage: 'private', destination: 'in_ontario', rule: 'Up to 100 days; refill once 2/3 of the last supply is used, unless an override is on file' },
  { coverage: 'private', destination: 'outside_ontario', rule: 'Up to 100 days; refill once 2/3 of the last supply is used, unless an override is on file' },
  { coverage: 'tdp', destination: 'in_ontario', rule: 'Up to 100 days' },
  { coverage: 'tdp', destination: 'outside_ontario', rule: 'Up to 200 days, only for a fill from 1 Aug to 31 Jan; otherwise 100 days' },
  { coverage: 'odb', destination: 'in_ontario', rule: 'Up to 100 days' },
  { coverage: 'odb', destination: 'outside_ontario', rule: '100 more days when more than 30 days are on hand; otherwise 200 days in total. 100 days once the vacation supply was used this benefit year' },
];

export interface VacationTripInput {
  /** The pharmacy's today (`getPharmacyTodayString`). */
  today: string;
  departureDate: string | null;
  returnDate: string | null;
  bufferDays: number | null;
  destination: Destination | null;
  coverage: Coverage | null;
  /** Days. Null means no cap beyond the coverage. */
  maxSingleFillDays: number | null;
  travelLetterOnFile: boolean;
  overrideOnFile: boolean;
  odbVacationUsed: boolean;
  usedSupplyPolicy: UsedSupplyPolicy;
}

export interface VacationMedInput {
  rxNumber: string;
  drugName: string;
  selected: boolean;
  lastFillDate: string | null;
  daysSupply: number | null;
  /** Disp qty ÷ days supply. Null when either is missing. */
  perDay: number | null;
  onHandQty: number | null;
}

export type VacationRowStatus = 'not_selected' | 'cannot_calculate' | 'enough_on_hand' | 'fill' | 'short';

export interface VacationRowResult {
  rxNumber: string;
  drugName: string;
  selected: boolean;
  status: VacationRowStatus;
  /** Why the row can't be calculated, or by how much it falls short. */
  reason: string | null;
  perDay: number | null;
  onHandQty: number | null;
  daysOnHand: number | null;
  lastFillDate: string | null;
  daysSupply: number | null;
  runsOut: string | null;
  earliestRefill: string | null;
  bridgeDays: number;
  bridgeQty: number;
  /** The coverage rule applied to this row's fill; null when it needs none. */
  ruleDescription: string | null;
  fillDays: number;
  fillQty: number;
  shortDays: number;
}

export type VacationWarningCode =
  | 'odb_second_early_fill'
  | 'odb_used_capped'
  | 'tdp_outside_window'
  | 'fill_after_departure'
  | 'no_travel_letter'
  | 'nothing_selected';

export interface VacationWarning {
  code: VacationWarningCode;
  severity: 'error' | 'warning';
  message: string;
}

export interface CoverageRuleResult {
  /** The most days this fill may cover. */
  capDays: number;
  description: string;
  outsideTdpWindow: boolean;
}

export type VacationPlan =
  | { status: 'cannot_calculate'; reasons: string[]; rows: VacationRowResult[]; rulesVersion: string }
  | {
      status: 'ok';
      /** Null when nothing ticked needs a fill. */
      recommendedFillDate: string | null;
      bridgeNeeded: boolean;
      /** Return date + buffer: the supply must last until this day. */
      needEndDate: string;
      /** The distinct rules applied across the rows, in row order. */
      rules: string[];
      rows: VacationRowResult[];
      warnings: VacationWarning[];
      rulesVersion: string;
    };

// ---- Date maths on YYYY-MM-DD strings ----

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** A real calendar day in YYYY-MM-DD (2026-02-31 is not one). */
export function isDateString(value: string | null | undefined): value is string {
  if (!value || !DATE_RE.test(value)) return false;
  const parsed = parseDateOnly(value);
  return !!parsed && format(parsed, 'yyyy-MM-dd') === value;
}

export function addDaysTo(date: string, days: number): string {
  return format(addDays(parseDateOnly(date) as Date, days), 'yyyy-MM-dd');
}

/** Whole days from `from` to `to` (positive when `to` is later). */
export function daysBetween(from: string, to: string): number {
  return differenceInCalendarDays(parseDateOnly(to) as Date, parseDateOnly(from) as Date);
}

const laterOf = (a: string, b: string): string => (a > b ? a : b);

/** TDP's out-of-province window: a fill from 1 Aug to 31 Jan (wraps the year). */
export function inTdpWindow(date: string): boolean {
  const monthDay = date.slice(5);
  return monthDay >= '08-01' || monthDay <= '01-31';
}

/** The benefit year a date falls in, 1 Aug to 31 Jul (interpretation, ADR-115). */
export function benefitYearOf(date: string): { start: string; end: string } {
  const year = Number(date.slice(0, 4));
  const startYear = date.slice(5) >= '08-01' ? year : year - 1;
  return { start: `${startYear}-08-01`, end: `${startYear + 1}-07-31` };
}

// ---- The rule table ----

export function coverageRuleFor(input: {
  coverage: Coverage;
  destination: Destination;
  /** Unfloored days on hand at the fill, so 30.5 is over 30. */
  onHandDaysAtFill: number;
  fillDate: string;
  odbVacationUsed: boolean;
  usedSupplyPolicy: UsedSupplyPolicy;
}): CoverageRuleResult {
  const { coverage, destination, onHandDaysAtFill, fillDate, odbVacationUsed, usedSupplyPolicy } = input;
  const outside = destination === 'outside_ontario';
  switch (coverage) {
    case 'cash':
      return { capDays: 365, description: 'Cash: up to 365 days', outsideTdpWindow: false };
    case 'private':
      return { capDays: 100, description: 'Private: up to 100 days', outsideTdpWindow: false };
    case 'tdp':
      if (!outside) return { capDays: 100, description: 'TDP in Ontario: up to 100 days', outsideTdpWindow: false };
      if (inTdpWindow(fillDate)) {
        return { capDays: 200, description: 'TDP outside Ontario: up to 200 days (1 Aug to 31 Jan)', outsideTdpWindow: false };
      }
      return {
        capDays: 100,
        description: 'TDP outside Ontario outside 1 Aug to 31 Jan: up to 100 days',
        outsideTdpWindow: true,
      };
    case 'odb':
      if (odbVacationUsed && usedSupplyPolicy === 'cap') {
        return {
          capDays: USED_SUPPLY_CAP_DAYS,
          description: `ODB, vacation supply already used this benefit year: limited to ${USED_SUPPLY_CAP_DAYS} days`,
          outsideTdpWindow: false,
        };
      }
      if (!outside) return { capDays: 100, description: 'ODB in Ontario: up to 100 days', outsideTdpWindow: false };
      if (odbVacationUsed) {
        return {
          capDays: 100,
          description: 'ODB outside Ontario, vacation supply already used this benefit year: up to 100 days',
          outsideTdpWindow: false,
        };
      }
      if (onHandDaysAtFill > 30) {
        return { capDays: 100, description: 'ODB outside Ontario, more than 30 days on hand: 100 more days', outsideTdpWindow: false };
      }
      return {
        capDays: Math.max(0, Math.floor(200 - onHandDaysAtFill)),
        description: 'ODB outside Ontario, 30 days or fewer on hand: 200 days in total',
        outsideTdpWindow: false,
      };
  }
}

// ---- Rows ----

const EPS = 1e-9;
const isPositive = (n: number | null): n is number => n != null && Number.isFinite(n) && n > 0;

function baseRow(med: VacationMedInput, trip: VacationTripInput): VacationRowResult {
  const row: VacationRowResult = {
    rxNumber: med.rxNumber,
    drugName: med.drugName,
    selected: med.selected,
    status: med.selected ? 'fill' : 'not_selected',
    reason: null,
    perDay: isPositive(med.perDay) ? med.perDay : null,
    onHandQty: med.onHandQty,
    daysOnHand: null,
    lastFillDate: isDateString(med.lastFillDate) ? med.lastFillDate : null,
    daysSupply: med.daysSupply,
    runsOut: null,
    earliestRefill: null,
    bridgeDays: 0,
    bridgeQty: 0,
    ruleDescription: null,
    fillDays: 0,
    fillQty: 0,
    shortDays: 0,
  };
  const cannot = (reason: string) => {
    if (med.selected) row.status = 'cannot_calculate';
    row.reason = reason;
    return row;
  };

  if (row.perDay == null) return cannot("No daily dose: the last fill has no days supply or dispensed quantity");
  if (med.onHandQty == null || !Number.isFinite(med.onHandQty)) return cannot('No quantity on hand: enter it');
  if (med.onHandQty < 0) return cannot("Qty on hand can't be negative");
  row.daysOnHand = Math.floor(med.onHandQty / row.perDay + EPS);
  row.runsOut = addDaysTo(trip.today, row.daysOnHand);

  if (trip.coverage === 'private' && !trip.overrideOnFile) {
    if (!row.lastFillDate || !isPositive(med.daysSupply)) {
      return cannot('No last fill date or days supply for the 2/3 rule');
    }
    row.earliestRefill = laterOf(trip.today, addDaysTo(row.lastFillDate, Math.ceil((2 / 3) * med.daysSupply - EPS)));
  } else if (trip.coverage) {
    row.earliestRefill = trip.today;
  }
  return row;
}

function tripReasons(trip: VacationTripInput): string[] {
  const reasons: string[] = [];
  const departure = trip.departureDate;
  const back = trip.returnDate;
  if (!departure) reasons.push('Enter the departure date');
  else if (!isDateString(departure)) reasons.push('The departure date is not a real date');
  if (!back) reasons.push('Enter the return date');
  else if (!isDateString(back)) reasons.push('The return date is not a real date');
  if (isDateString(departure) && isDateString(back)) {
    if (back < departure) reasons.push('The return date is before the departure date');
  }
  if (isDateString(departure) && departure < trip.today) reasons.push('The departure date is in the past');
  if (trip.bufferDays == null || !Number.isFinite(trip.bufferDays)) reasons.push('Enter the buffer days');
  else if (trip.bufferDays < 0 || !Number.isInteger(trip.bufferDays)) reasons.push('Buffer days must be a whole number, 0 or more');
  if (!trip.destination) reasons.push('Choose the destination');
  if (!trip.coverage) reasons.push('Choose the coverage');
  if (trip.maxSingleFillDays != null && !(Number.isInteger(trip.maxSingleFillDays) && trip.maxSingleFillDays > 0)) {
    reasons.push('Max single fill must be a whole number of days, more than 0');
  }
  return reasons;
}

/**
 * The plan: everything ticked goes in one main fill, on the latest date any
 * ticked drug that needs a fill may be refilled. A drug that runs out before
 * that date gets a bridge fill to carry it to the main fill.
 *
 * The supply must last from today until return date + buffer days.
 */
export function planVacationSupply(trip: VacationTripInput, meds: VacationMedInput[]): VacationPlan {
  const rows = meds.map((med) => baseRow(med, trip));
  const reasons = tripReasons(trip);
  if (reasons.length > 0) {
    return { status: 'cannot_calculate', reasons, rows, rulesVersion: VACATION_RULES_VERSION };
  }
  const coverage = trip.coverage as Coverage;
  const destination = trip.destination as Destination;
  const departure = trip.departureDate as string;
  const needEndDate = addDaysTo(trip.returnDate as string, trip.bufferDays as number);
  const needFromToday = daysBetween(trip.today, needEndDate);

  const warnings: VacationWarning[] = [];
  const selected = rows.filter((r) => r.selected);
  if (selected.length === 0) {
    warnings.push({ code: 'nothing_selected', severity: 'warning', message: 'Tick the medications to include' });
  }

  const needing = selected.filter((r) => r.status !== 'cannot_calculate' && (r.daysOnHand as number) < needFromToday);
  for (const r of selected) {
    if (r.status !== 'cannot_calculate' && !needing.includes(r)) r.status = 'enough_on_hand';
  }

  const recommendedFillDate =
    needing.length > 0 ? needing.map((r) => r.earliestRefill as string).reduce(laterOf, trip.today) : null;

  const rules: string[] = [];
  if (recommendedFillDate) {
    const needFromFill = daysBetween(recommendedFillDate, needEndDate);
    for (const r of needing) {
      const perDay = r.perDay as number;
      const gap = daysBetween(r.runsOut as string, recommendedFillDate);
      if (gap > 0) {
        r.bridgeDays = gap;
        r.bridgeQty = Math.ceil(gap * perDay - EPS);
      }
      const onHandDaysAtFill = Math.max(0, -gap);
      // The rule branch reads the unfloored days (review L): 30.5 days is over 30.
      const exactDaysAtFill = Math.max(
        0,
        (r.onHandQty as number) / perDay - daysBetween(trip.today, recommendedFillDate),
      );
      const rowRule = coverageRuleFor({
        coverage,
        destination,
        onHandDaysAtFill: gap > 0 ? 0 : exactDaysAtFill,
        fillDate: recommendedFillDate,
        odbVacationUsed: trip.odbVacationUsed,
        usedSupplyPolicy: trip.usedSupplyPolicy,
      });
      r.ruleDescription = rowRule.description;
      if (!rules.includes(rowRule.description)) rules.push(rowRule.description);
      const cap = trip.maxSingleFillDays != null ? Math.min(rowRule.capDays, trip.maxSingleFillDays) : rowRule.capDays;
      const shortfall = Math.max(0, needFromFill - onHandDaysAtFill);
      r.fillDays = Math.min(shortfall, cap);
      r.fillQty = Math.ceil(r.fillDays * perDay - EPS);
      r.shortDays = shortfall - r.fillDays;
      if (r.shortDays > 0) {
        r.status = 'short';
        r.reason = `Covers ${r.shortDays} ${r.shortDays === 1 ? 'day' : 'days'} less than the trip needs`;
      } else {
        r.status = 'fill';
      }
    }
    if (recommendedFillDate > departure) {
      warnings.push({
        code: 'fill_after_departure',
        severity: 'error',
        message: 'The earliest fill date is after departure. A plan override is needed to fill before the trip.',
      });
    }
    if (coverage === 'tdp' && destination === 'outside_ontario' && !inTdpWindow(recommendedFillDate)) {
      warnings.push({
        code: 'tdp_outside_window',
        severity: 'warning',
        message: 'TDP allows the 200-day supply outside Ontario only for a fill from 1 Aug to 31 Jan. 100 days apply.',
      });
    }
  }

  if (coverage === 'odb' && trip.odbVacationUsed) {
    warnings.push(
      trip.usedSupplyPolicy === 'cap'
        ? {
            code: 'odb_used_capped',
            severity: 'warning',
            message: `ODB vacation supply already used this benefit year: the supply is limited to ${USED_SUPPLY_CAP_DAYS} days. A patient can always take up to ${USED_SUPPLY_CAP_DAYS} days; the vacation supply is anything above that.`,
          }
        : { code: 'odb_second_early_fill', severity: 'error', message: 'A second early fill will reject' },
    );
  }
  if (destination === 'outside_ontario' && !trip.travelLetterOnFile) {
    warnings.push({ code: 'no_travel_letter', severity: 'warning', message: 'No travel letter or insurance on file' });
  }

  return {
    status: 'ok',
    recommendedFillDate,
    bridgeNeeded: needing.some((r) => r.bridgeQty > 0),
    needEndDate,
    rules,
    rows,
    warnings,
    rulesVersion: VACATION_RULES_VERSION,
  };
}
